import { useMemo, useState } from "react";
import {
	Box,
	Button,
	InputAdornment,
	Paper,
	Skeleton,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
	CheckCircleOutlineRounded,
	ErrorOutlineRounded,
	ReplayRounded,
	Search,
	TimerOutlined,
	WarningAmberRounded,
} from "@mui/icons-material";
import { useCentralAlertas } from "../context";
import AnalisePadrao from "../analisePadrao";
import ModalNewTicket from "../../tickets/modalNewTicket";
import ModalNewTicketIxc from "../../tickets/modalNewTicketIxc";
import ModalCriarOs from "../modalCriarOs";
import { ERP_ATIVO, ERPS_COM_TICKET } from "../config/formTicketParams";
import dayjs from "dayjs";
import {
	CATEGORIAS_INTELIGENTES,
	LABEL_CATEGORIA_INTELIGENTE,
	LABEL_SEVERIDADE,
	SEVERIDADES,
	formatarDuracaoCurta,
} from "../../../utils/centralDeAlertas/labels";
import {
	corCategoriaInteligente,
	corSeveridade,
} from "../../../utils/centralDeAlertas/cores";
import useStyles from "./styles";
import { GUIDES_CENTRAL, MetricInfo } from "../metricInfo";

// Fila de ação (Alertas inteligentes): grade priorizada de detecções automáticas (zona morta,
// OS faltante, falha recorrente) — uma linha por equipamento afetado, com
// contexto curto e ação. Todas as ações são reais e passam por um modal de
// confirmação antes de tocar o ERP:
// - falha recorrente -> AnalisePadrao (só leitura: histórico de 30 dias);
// - zona morta       -> ModalNewTicket, o modal padrão do sistema (cria
//                       atendimento no ERP, com cliente/serviço pré-selecionados);
// - OS faltante      -> ModalCriarOs (cria OS vinculada ao atendimento).
// Nenhuma delas escreve no Zabbix.

const QUANTIDADE_SKELETONS = 6;
// Idade (min) a partir da qual o tempo em aberto ganha cor de alerta.
const MINUTOS_ALERTA_LARANJA = 4 * 60;
const MINUTOS_ALERTA_VERMELHO = 24 * 60;

// A ação de OS faltante usa o ModalCriarOs, que tem formulário por ERP
// (HubSoft via paramsNewOs; IXC via paramsNewTicket + funcionários por
// setor). VERO é somente leitura (sem criação de demanda/OS na API) e
// VOALLE/BRILHA não têm integração de escrita — o backend rejeita com 400
// (utils/erp/capacidadesErp.js); aqui o botão nem habilita.
const ERPS_COM_OS_CENTRAL = ["HUBSOFT", "IXC"];

const TOOLTIP_ACAO_INDISPONIVEL = {
	zona_morta: (erp) =>
		`O ERP ${erp} não suporta abertura de atendimento pelo sistema`,
	os_faltante: (erp) => `O ERP ${erp} não suporta criação de OS pelo sistema`,
};

// "SLA em risco" está fora da lista por enquanto: o único SLA que existe hoje
// é o por ETAPA do fluxo de atendimento (tela /interno/suporte/atendimentos,
// modal "Configuração de SLA por etapa"), que mede tempo na coluna do board —
// semântica diferente do SLA contratado que esta categoria pressupõe. O
// backend também não a detecta (ver incidentesEventos.js). Para religar:
// remover o filtro abaixo — rótulo, cor, ícone e ação já existem.
const CATEGORIAS_EXIBIDAS = CATEGORIAS_INTELIGENTES.filter(
	(categoria) => categoria !== "sla_em_risco"
);

const ICONE_CATEGORIA = {
	zona_morta: WarningAmberRounded,
	os_faltante: ErrorOutlineRounded,
	falha_recorrente: ReplayRounded,
	sla_em_risco: TimerOutlined,
};

const ACAO_CATEGORIA = {
	zona_morta: {
		rotulo: "Abrir atendimento",
		mensagem: (nomeEquipamento) =>
			`Abertura de atendimento sugerida para ${nomeEquipamento}`,
	},
	os_faltante: {
		rotulo: "Criar OS",
		mensagem: (nomeEquipamento) =>
			`Criação de OS sugerida para ${nomeEquipamento}`,
	},
	falha_recorrente: {
		rotulo: "Analisar padrão",
		mensagem: (nomeEquipamento) =>
			`Análise de padrão iniciada para ${nomeEquipamento}`,
	},
	sla_em_risco: {
		rotulo: "Escalar SLA",
		mensagem: (nomeEquipamento) => `SLA escalado para ${nomeEquipamento}`,
	},
};

export default function AlertasInteligentes() {
	const theme = useTheme();
	const styles = useStyles(theme);
	const {
		agora,
		carregando,
		incidentesFiltrados,
		mostrarToast,
		tipoErp,
		erpAplicado,
	} = useCentralAlertas();
	const [aba, setAba] = useState("todas");
	const [busca, setBusca] = useState("");
	// Incidente cuja falha recorrente está sendo analisada no modal
	// (null = modal fechado).
	const [incidenteAnalisado, setIncidenteAnalisado] = useState(null);
	// Incidente para o qual o operador está abrindo atendimento (zona morta).
	const [incidenteAtendimento, setIncidenteAtendimento] = useState(null);
	// Incidente para o qual o operador está criando OS (OS faltante).
	const [incidenteOs, setIncidenteOs] = useState(null);

	const deteccoes = useMemo(() => {
		const itens = [];
		CATEGORIAS_EXIBIDAS.forEach((categoria) => {
			const incidentes = incidentesFiltrados.filter((incidente) =>
				incidente.categoriasInteligentes.includes(categoria)
			);
			if (categoria === "falha_recorrente") {
				// Vários alertas do mesmo equipamento viram uma única linha,
				// com a contagem de alertas no texto de apoio.
				const porEquipamento = new Map();
				incidentes.forEach((incidente) => {
					const equipamentoId = incidente.alerta.equipamentoId;
					const existente = porEquipamento.get(equipamentoId);
					if (existente) existente.alertas += 1;
					else porEquipamento.set(equipamentoId, { categoria, incidente, alertas: 1 });
				});
				itens.push(...porEquipamento.values());
				return;
			}
			incidentes.forEach((incidente) => itens.push({ categoria, incidente }));
		});
		// Prioridade: em aberto > categoria (ordem do catálogo) > severidade >
		// mais antigo primeiro.
		return itens.sort(
			(a, b) =>
				Boolean(b.incidente.aberto) - Boolean(a.incidente.aberto) ||
				CATEGORIAS_EXIBIDAS.indexOf(a.categoria) -
					CATEGORIAS_EXIBIDAS.indexOf(b.categoria) ||
				SEVERIDADES.indexOf(a.incidente.severidade) -
					SEVERIDADES.indexOf(b.incidente.severidade) ||
				dayjs(a.incidente.dataHoraQueda).valueOf() -
					dayjs(b.incidente.dataHoraQueda).valueOf(),
		);
	}, [incidentesFiltrados]);

	// Ações que criam registro no ERP habilitam conforme o ERP do painel —
	// mesmo espelho dos gates do backend (utils/erp/capacidadesErp.js). Ações
	// de leitura (analisar padrão) valem para todos os ERPs.
	const erp = tipoErp || ERP_ATIVO();
	const acaoDisponivel = {
		zona_morta: ERPS_COM_TICKET.includes(erp),
		os_faltante: ERPS_COM_OS_CENTRAL.includes(erp),
		falha_recorrente: true,
		sla_em_risco: true,
	};

	// Cada ação abre seu próprio modal — as que criam registro no ERP
	// (atendimento/OS) nunca disparam direto do clique na lista.
	const executarAcao = (categoria, incidente, nomeEquipamento) => {
		if (!acaoDisponivel[categoria]) return;
		if (categoria === "falha_recorrente") {
			setIncidenteAnalisado(incidente);
			return;
		}
		if (categoria === "zona_morta") {
			setIncidenteAtendimento(incidente);
			return;
		}
		if (categoria === "os_faltante") {
			setIncidenteOs(incidente);
			return;
		}
		const acao = ACAO_CATEGORIA[categoria];
		console.log(`[central-de-alertas] ${acao.rotulo}`, incidente);
		mostrarToast(acao.mensagem(nomeEquipamento));
	};

	const termo = busca.trim().toLowerCase();
	const linhasFila = deteccoes
		.filter((deteccao) => aba === "todas" || deteccao.categoria === aba)
		.filter(({ incidente }) =>
			!termo ||
			[incidente.equipamento?.nome, incidente.cliente?.nome, incidente.alerta?.titulo]
				.some((campo) => (campo || "").toLowerCase().includes(termo)),
		);

	const abas = [
		{ chave: "todas", rotulo: "Todas", total: deteccoes.length, cor: theme.palette.text.secondary },
		...CATEGORIAS_EXIBIDAS.map((categoria) => ({
			chave: categoria,
			rotulo: LABEL_CATEGORIA_INTELIGENTE[categoria],
			total: deteccoes.filter((d) => d.categoria === categoria).length,
			cor: corCategoriaInteligente(theme, categoria),
		})),
	];

	// Tempo da linha: em aberto conta desde a queda (cor escala com a idade);
	// recorrência mostra o período; encerrado mostra a duração.
	const tempoDaLinha = ({ categoria, incidente }) => {
		if (categoria === "falha_recorrente") {
			return { texto: "no período", cor: theme.palette.text.secondary, forte: false };
		}
		if (incidente.aberto) {
			const minutos = agora.diff(dayjs(incidente.dataHoraQueda), "minute", true);
			const cor =
				minutos >= MINUTOS_ALERTA_VERMELHO
					? corSeveridade(theme, "critico")
					: minutos >= MINUTOS_ALERTA_LARANJA
						? corSeveridade(theme, "alto")
						: theme.palette.text.primary;
			return { texto: `há ${formatarDuracaoCurta(minutos)}`, cor, forte: minutos >= MINUTOS_ALERTA_VERMELHO };
		}
		return { texto: formatarDuracaoCurta(incidente.mttr), cor: theme.palette.text.secondary, forte: false };
	};

	const renderBotao = (categoria, incidente, nomeEquipamento) => {
		const primario = categoria === "zona_morta" && incidente.aberto;
		const botao = (
			<Button
				size="small"
				disabled={!acaoDisponivel[categoria]}
				onClick={() => executarAcao(categoria, incidente, nomeEquipamento)}
				sx={styles.botaoAcao(primario)}
			>
				{ACAO_CATEGORIA[categoria].rotulo}
			</Button>
		);
		if (acaoDisponivel[categoria]) return botao;
		// span obrigatório: Tooltip do MUI não dispara em elemento disabled.
		return (
			<Tooltip title={TOOLTIP_ACAO_INDISPONIVEL[categoria]?.(erp) || ""}>
				<span>{botao}</span>
			</Tooltip>
		);
	};

	return (
		<Paper sx={styles.container}>
			<Box sx={styles.linhaCabecalho}>
				<Box sx={styles.cabecalho}>
					<Typography variant="mySubtitle">Fila de ação</Typography>
					{!carregando && deteccoes.length > 0 && (
						<Box sx={styles.badgeTotal}>{deteccoes.length}</Box>
					)}
				</Box>
				<Box sx={styles.cabecalho}>
					<TextField
						size="small"
						value={busca}
						onChange={(evento) => setBusca(evento.target.value)}
						placeholder="Filtrar fila…"
						sx={styles.campoBusca}
						inputProps={{ "aria-label": "Filtrar fila de ação" }}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<Search fontSize="small" />
								</InputAdornment>
							),
						}}
					/>
					<MetricInfo theme={theme} guide={GUIDES_CENTRAL.alertasInteligentes} />
				</Box>
			</Box>
			{carregando ? (
				<Box sx={styles.lista}>
					{Array.from({ length: QUANTIDADE_SKELETONS }, (_, indice) => (
						<Skeleton key={indice} variant="rounded" sx={styles.skeletonItem} />
					))}
				</Box>
			) : deteccoes.length === 0 ? (
				<Box sx={styles.estadoVazio}>
					<CheckCircleOutlineRounded sx={styles.iconeVazio} />
					<Typography variant="caption" sx={styles.textoVazio}>
						Nenhuma detecção automática no período
					</Typography>
				</Box>
			) : (
				<>
					<Box sx={styles.abas}>
						{abas.map(({ chave, rotulo, total, cor }) => (
							<Box
								key={chave}
								component="button"
								type="button"
								onClick={() => setAba(chave)}
								sx={styles.aba(aba === chave)}
							>
								<Box sx={styles.pontoAba(cor)} />
								{rotulo}
								<Box component="span" sx={styles.totalAba}>{total}</Box>
							</Box>
						))}
					</Box>
					<Box sx={styles.cabecalhoGrade}>
						<span />
						<span>Equipamento · alerta</span>
						<span>Cliente</span>
						<span>Situação</span>
						<span>Tempo</span>
						<span>Qtd</span>
						<span />
					</Box>
					<Box sx={styles.lista}>
						{linhasFila.length === 0 && (
							<Typography variant="caption" sx={styles.textoVazio}>
								Nenhuma detecção para o filtro.
							</Typography>
						)}
						{linhasFila.map((deteccao) => {
							const { categoria, incidente } = deteccao;
							const cor = corCategoriaInteligente(theme, categoria);
							const corSev = corSeveridade(theme, incidente.severidade);
							const Icone = ICONE_CATEGORIA[categoria];
							const nomeEquipamento = incidente.equipamento?.nome || "—";
							const tempo = tempoDaLinha(deteccao);
							const quantidade =
								categoria === "falha_recorrente"
									? deteccao.alertas
									: incidente.alerta?.alertasAgrupados?.length || 1;

							return (
								<Box
									key={`${categoria}-${incidente.id}`}
									sx={styles.linhaGrade(incidente.aberto && categoria !== "falha_recorrente")}
								>
									<Tooltip title={LABEL_SEVERIDADE[incidente.severidade] || ""} arrow>
										<Box sx={styles.barraSeveridade(corSev)} />
									</Tooltip>
									<Box sx={styles.textosItem}>
										<Typography sx={styles.tituloItem}>{nomeEquipamento}</Typography>
										<Tooltip title={incidente.alerta?.titulo || ""} arrow placement="bottom-start">
											<Typography variant="caption" sx={styles.descricaoItem}>
												{incidente.alerta?.titulo || "—"}
											</Typography>
										</Tooltip>
									</Box>
									<Typography variant="caption" sx={styles.descricaoItem}>
										{incidente.cliente?.nome || "—"}
									</Typography>
									<Box>
										<Box sx={styles.chipSituacao(cor)}>
											<Icone />
											{LABEL_CATEGORIA_INTELIGENTE[categoria]}
										</Box>
									</Box>
									<Typography sx={styles.textoNumero(tempo.cor, tempo.forte)}>
										{tempo.texto}
									</Typography>
									<Typography sx={styles.textoNumero(theme.palette.text.secondary)}>
										{`×${quantidade}`}
									</Typography>
									{renderBotao(categoria, incidente, nomeEquipamento)}
								</Box>
							);
						})}
					</Box>
					<Typography variant="caption" sx={styles.rodape}>
						Ordenado por situação, severidade e tempo em aberto
					</Typography>
				</>
			)}
			<AnalisePadrao
				aberto={Boolean(incidenteAnalisado)}
				equipamento={incidenteAnalisado?.equipamento}
				cliente={incidenteAnalisado?.cliente}
				onFechar={() => setIncidenteAnalisado(null)}
				erpId={erpAplicado}
			/>
			{/* Abertura de atendimento usa o MODAL DO SISTEMA (o mesmo de
			    /interno/suporte/*), que já pré-seleciona cliente e serviço pelas
			    props e carrega todo o fluxo padrão (validações, OS junto,
			    anexos). A Central só informa de qual alerta veio. O modal é o
			    do ERP ativo (mesmo branch das telas de tickets); ERPs sem
			    criação nem chegam aqui — a ação fica desabilitada. */}
			{incidenteAtendimento && erp === "HUBSOFT" && (
				<ModalNewTicket
					open
					handleClose={() => setIncidenteAtendimento(null)}
					refrashTIckets={() => {
						setIncidenteAtendimento(null);
						mostrarToast("Atendimento aberto no ERP.");
					}}
					clienteErpId={incidenteAtendimento?.alerta?.clienteErpId ?? null}
					serviceId={
						incidenteAtendimento?.alerta?.servicoId
							? Number(incidenteAtendimento.alerta.servicoId)
							: null
					}
					erp={erp}
					erpId={erpAplicado}
				/>
			)}
			{incidenteAtendimento && erp === "IXC" && (
				<ModalNewTicketIxc
					open
					handleClose={() => setIncidenteAtendimento(null)}
					refrashTIckets={() => {
						setIncidenteAtendimento(null);
						mostrarToast("Atendimento aberto no ERP.");
					}}
					clienteErpId={incidenteAtendimento?.alerta?.clienteErpId ?? null}
					serviceId={
						incidenteAtendimento?.alerta?.servicoId
							? Number(incidenteAtendimento.alerta.servicoId)
							: null
					}
					erp={erp}
					erpId={erpAplicado}
				/>
			)}
			<ModalCriarOs
				aberto={Boolean(incidenteOs)}
				incidente={incidenteOs}
				onFechar={() => setIncidenteOs(null)}
				erp={erp}
				erpId={erpAplicado}
				onSucesso={(os) => {
					setIncidenteOs(null);
					mostrarToast(
						`OS ${os?.id_ordem_servico ? `#${os.id_ordem_servico} ` : ""}criada no ERP.`,
					);
				}}
			/>
		</Paper>
	);
}
